/**
 * weather_forecast — the Commons weather, N hours ahead, at one spot (sweep 2026-10-05). Pure: it calls the SAME
 * `resolveWeather` / `meteoAt` the renderer calls, at a later clock — it re-implements nothing.
 *
 *   WHAT DEPENDS ON TIME   `meteoAt(seed, x, z, tSec, tod)`: the fronts ADVECT with the shared wall clock `tSec`
 *                          (FRONT_ADVECT_M_PER_S along the prevailing wind), and the diurnal cycle reads `tod` (0..1,
 *                          the sky row's time of day, which turns once per `cycleSeconds` — 7200 s by default; a world
 *                          whose clock is HELD (Edit Mode `timeMode: fixed`) keeps its `tod`).
 *   WHAT IS PINNED         an `override` (the live-ops `sky_state.weather` unless it is 'clear', or the Local debug
 *                          pin — `pickWeatherOverride`) forces the render weather: there is no natural field to
 *                          forecast, so a pinned weather is REPORTED as held, never forecast. (The office's pin can roll
 *                          at the server's day boundary; the forecast does not pretend to know that.)
 *   THE HAZARD             `hazardKind` over the resolver's own outputs — the storm-hazard module's thresholds.
 */
import { meteoAt, resolveWeather, type WeatherClass } from './weather.js';
import { hazardKind, hazardLabel, severity, type HazardInput, type HazardKind } from './storm_hazards.js';

export const FORECAST_HOURS: readonly number[] = [0, 1, 3, 6];
/** The tab refreshes at most this often while open. */
export const FORECAST_REFRESH_MS = 30_000;
/** The server's DAY_CYCLE_SECONDS (a sky row with no cycle of its own). */
export const DEFAULT_CYCLE_S = 7200;

export interface ForecastSlot {
  hours: number;
  label: string;
  kind: WeatherClass;
  /** Precipitation or dust strength 0..1 (0 for a dry sky). */
  intensity: number;
  wind01: number;
  windWords: string;
  tempC: number;
  /** Cloud cover 0..1 (the resolver's). */
  cover01: number;
  hazard: HazardKind;
  hazardWords: string;
  hazardSeverity: number;
}

export interface ForecastInput {
  seed: string; x: number; z: number;
  /** Shared wall clock now, seconds. */
  nowSec: number;
  /** Time of day now, 0..1. */
  tod: number;
  cycleSeconds: number;
  /** A held clock (Edit Mode fixed time): tod does not advance. */
  heldClock?: boolean;
  /** The pin that would be in force (`pickWeatherOverride(debug, commons)`), if any. */
  override?: string;
  /** Who holds the pin, for the words. */
  overrideBy?: 'office' | 'debug';
}

export type ForecastResult =
  | { held: false; slots: ForecastSlot[] }
  | { held: true; by: 'office' | 'debug'; weather: string; slots: [] };

/** The time of day `hours` from now (wraps; a held clock stays). */
export function forecastTod(tod: number, cycleSeconds: number, hours: number, held = false): number {
  if (held) return tod;
  const cyc = Number.isFinite(cycleSeconds) && cycleSeconds > 0 ? cycleSeconds : DEFAULT_CYCLE_S;
  const t = tod + (hours * 3600) / cyc;
  return ((t % 1) + 1) % 1;
}

export function windWords(w01: number): string {
  if (w01 < 0.15) return 'Calm';
  if (w01 < 0.35) return 'Light breeze';
  if (w01 < 0.55) return 'Breezy';
  if (w01 < 0.75) return 'Windy';
  return 'Gale';
}

export function hoursLabel(h: number): string { return h <= 0 ? 'Now' : `+${h} h`; }

/** One slot at `hours` ahead. */
export function forecastSlot(i: ForecastInput, hours: number): ForecastSlot {
  const t = i.nowSec + hours * 3600;
  const tod = forecastTod(i.tod, i.cycleSeconds, hours, i.heldClock);
  const m = meteoAt(i.seed, i.x, i.z, t, tod);
  const r = resolveWeather(i.seed, i.x, i.z, t, undefined, tod);
  // The class: what the sky DOES (the resolver's particles), else what it looks like (the meteo's fog / cover).
  let kind: WeatherClass;
  if (r.particleKind === 'dust') kind = 'sandstorm';
  else if (r.particleKind === 'snow') kind = 'snow';
  else if (r.particleKind === 'rain') kind = r.particleDensity >= 0.85 ? 'storm' : 'rain';
  else if (m.fog01 > 0.45) kind = 'fog';
  else if (r.cloudCoverage > 0.55) kind = 'cloudy';
  else kind = 'clear';
  const input: HazardInput = {
    kind: r.particleKind === 'none' ? '' : r.particleKind, density: r.particleDensity, wind: r.windStrength, dust: r.dust,
    temperatureC: m.temperatureC, sheltered: false, nearFire: false,
  };
  const hazard = hazardKind(input);
  return {
    hours, label: hoursLabel(hours), kind,
    intensity: r.particleKind === 'none' ? 0 : r.particleDensity,
    wind01: r.windStrength, windWords: windWords(r.windStrength),
    tempC: m.temperatureC, cover01: r.cloudCoverage,
    hazard, hazardWords: hazardLabel(hazard), hazardSeverity: severity(input),
  };
}

export function forecast(i: ForecastInput, hours: readonly number[] = FORECAST_HOURS): ForecastResult {
  if (i.override) return { held: true, by: i.overrideBy ?? 'office', weather: i.override, slots: [] };
  return { held: false, slots: hours.map((h) => forecastSlot(i, h)) };
}

/** The first slot ahead (hours > 0) carrying a hazard, or null — the tab's headline warning. */
export function firstHazard(slots: readonly ForecastSlot[]): ForecastSlot | null {
  return slots.find((s) => s.hours > 0 && s.hazard !== '') ?? null;
}

export function tempText(c: number): string { return `${Math.round(c)}°C`; }
export function percentText(v01: number): string { return `${Math.round(Math.max(0, Math.min(1, v01)) * 100)}%`; }
